import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Typography,
  Grid,
  Chip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Snackbar,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Tooltip,
  CircularProgress,
  Menu,
  MenuItem,
  TextField,
} from '@mui/material';
import { DataGrid, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArchiveIcon from '@mui/icons-material/Archive';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Restore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PublishIcon from '@mui/icons-material/Publish';
import { getUserJobs, Campaign, archiveCampaign, deleteCampaign, updateCampaignStatus, uploadCampaign, checkCampaignDuplicates, fixCampaignCounts } from '../services/ecm-api';
import { getCurrentUser } from '../auth/LoginButton';
import {
  getUserRoles, ROLE_ROUTES, isDripOnlyUser, dripOnlyRedirect, retiredDripPath, DRIP_LEADS_PATH, DRIP_WEBSITE_PATH,
} from '../rbac/roles';
import ViewResponses from './ViewResponses';
import axios from '../api/axios';
import { ThemeSelector } from '../ThemeSelector';
import { getSelectedTheme, applyTheme } from '../themes';
import GraduateSurveys from './GraduateSurveys';
import NewCampaign from './NewCampaign';
import { SchedulerAdminDashboard } from './scheduler/SchedulerAdminDashboard';
import DripCampaigns from './DripCampaigns';
import VerityLeads from './VerityLeads';
import WebsiteUnrouted from './WebsiteUnrouted';
import EmailTemplates from './EmailTemplates';
import { canUsePurpose } from '../services/email-templates-api';
import { Tabs, Tab } from '@mui/material';

// Tab and sub-tab addresses. Each one must have a child route in App.tsx, or choosing it renders a
// blank page. PORTAL_PATH is the Campaign Portal tab itself, which has no sub-tabs: it is New Campaign
// alone. Staff make new dynamic templates in SendGrid itself, and New Campaign's pickers list them
// from there. DRIP_VIEW_PATHS are Drip Campaign's four sub-tab addresses.
const PORTAL_PATH = '/ECM/Dashboard/new-campaign';
// The retired Campaign Portal > SendGrid Templates sub-tab. Its route stays in App.tsx for old links,
// and the redirect below sends it to PORTAL_PATH.
const RETIRED_PORTAL_SUB = 'sendgrid-templates';
const DRIP_PATH = '/ECM/Dashboard/drip';
const DRIP_LAYOUT_PATH = '/ECM/Dashboard/drip/layout-template';

// The views inside Drip Campaign, in sub-tab order: its own two, then Verity Leads and Website Leads:
// not in Verity, which used to be top-level tabs. Their old addresses (Dashboard/leads,
// Dashboard/website) keep their routes and are redirected here (rbac/roles retiredDripPath).
type DripView = 'entries' | 'layout-template' | 'leads' | 'website';
const DRIP_VIEW_PATHS: Record<DripView, string> = {
  entries: DRIP_PATH,
  'layout-template': DRIP_LAYOUT_PATH,
  leads: DRIP_LEADS_PATH,
  website: DRIP_WEBSITE_PATH,
};

type EcmTab = 'campaigns' | 'graduates' | 'new_campaign' | 'scheduler' | 'drip';
/**
 * The address each top-level tab opens, in tab-bar order: the first one a user can see is where an
 * address they cannot see sends them. Each address must have a child route in App.tsx, or choosing
 * the tab renders a blank page.
 */
const TAB_PATHS: Record<EcmTab, string> = {
  campaigns: '/ECM/Dashboard',
  graduates: '/ECM/Dashboard/graduates',
  new_campaign: PORTAL_PATH,
  scheduler: '/ECM/Dashboard/scheduler',
  drip: DRIP_PATH,
};

/** The second row of tabs inside one top-level tab. Rendered only when there is a choice to make. */
function SubTabs<V extends string>({ label, value, tabs, onChange }: {
  label: string;
  value: V;
  tabs: Array<{ value: V; label: string }>;
  onChange: (value: V) => void;
}) {
  return (
    // Ultrawide tier: the same 40px inset as the screens below it (EmailTemplates, the drip
    // entries), so switching sub-tabs never moves the left edge.
    <Box sx={{ px: 3, mt: 1, '@media (min-width: 2400px)': { px: 5 } }}>
      <Tabs
        value={value}
        onChange={(_e, v: V) => onChange(v)}
        aria-label={`${label} sections`}
        variant="scrollable"
        scrollButtons="auto"
        TabIndicatorProps={{ style: { backgroundColor: 'var(--accent)' } }}
        sx={{ minHeight: 40, borderBottom: 1, borderColor: 'divider' }}
      >
        {tabs.map((t) => (
          <Tab
            key={t.value}
            value={t.value}
            label={t.label}
            sx={{
              minHeight: 40,
              py: 1,
              fontSize: '0.8125rem',
              color: 'var(--text-secondary)',
              '&.Mui-selected': { color: 'var(--accent)' },
            }}
          />
        ))}
      </Tabs>
    </Box>
  );
}

export default function ECMDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  // Graduate.Admin / Graduate.Viewer (without a full-ECM role) may ONLY use the Graduate Dashboard.
  const _ecmRoles = getUserRoles(getCurrentUser());
  const isGraduateOnly =
    (_ecmRoles.includes('Graduate.Admin') || _ecmRoles.includes('Graduate.Viewer')) &&
    !_ecmRoles.some((r: string) => ['Survey.Admin', 'Campaign.Manager'].includes(r));
  // Drip Campaign Manager is a narrow role: it exists to run drip campaigns, not to see every
  // campaign and graduate in the college. A Drip.Manager WITHOUT a broader ECM role therefore gets
  // the drip tab and nothing else - the same shape as isGraduateOnly above.
  const canSeeDrip = _ecmRoles.some((r: string) => ['Drip.Manager', 'Survey.Admin'].includes(r));
  // Shared with ECMRoutesWrapper, which seals the /ECM routes outside this component the same way.
  const isDripOnly = isDripOnlyUser(_ecmRoles);
  // The one SendGrid template screen SIMS still has: the drip layout, inside Drip Campaign, for the
  // role set the server enforces for that purpose (utils/email_templates.py ROLES, mirrored as
  // PURPOSE_ROLES): Survey.Admin and Drip.Manager. Campaign templates have no screen here; staff
  // create them in SendGrid.
  const canSeeDripLayout = canUsePurpose(_ecmRoles, 'drip_layout') && canSeeDrip && !isGraduateOnly;
  // Multi-role, non-admin users get a "My Dashboards" switcher back to the hub (/Dashboard) —
  // e.g. a Graduate.Admin + Survey.Viewer user can leave the Graduate Dashboard for their other
  // dashboard. Admins skip the hub, so it isn't shown to them.
  const _isAdminUser = _ecmRoles.some((r: string) => ['Survey.Admin', 'Survey.Agent', 'Scheduler.Admin'].includes(r));
  const showMyDashboards = !_isAdminUser && _ecmRoles.filter((r: string) => ROLE_ROUTES[r]).length > 1;
  const _pathParts = location.pathname.split('/');
  const _dashboardIdx = _pathParts.indexOf('Dashboard');
  const seg = _dashboardIdx >= 0 ? (_pathParts[_dashboardIdx + 1] ?? '') : '';
  const subSeg = _dashboardIdx >= 0 ? (_pathParts[_dashboardIdx + 2] ?? '') : '';
  // email-templates is the address of the retired top-level Templates tab. Old links still work: a
  // campaign role lands on Campaign Portal (New Campaign), a drip-only role on its layout template.
  const legacyTemplates = seg === 'email-templates';
  // The retired Verity Leads and Website Leads tab addresses: the Drip Campaign sub-tab each now lives
  // at (null for any other address). It renders from the first frame while the redirect moves the address.
  const movedTo = retiredDripPath(location.pathname);
  const movedView: DripView | null = movedTo === DRIP_LEADS_PATH ? 'leads' : movedTo === DRIP_WEBSITE_PATH ? 'website' : null;
  // Scheduler tab is Scheduler.Admin-ONLY. Check the FULL roles list — a user with
  // multiple roles must actually hold "Scheduler.Admin" to see it (not just their
  // primary role). (Note: real enforcement also needs the backend scheduler dev-auth
  // bypass fixed — see security follow-up.) It reads the same list as every other tab
  // check here, so the bar and the redirect below can never disagree about it.
  const canSeeScheduler = _ecmRoles.includes('Scheduler.Admin');
  // The tabs this user's bar shows. The bar, the tab picked below and the redirect all read this one
  // list, so a tab can never be selected - or its content rendered - without being shown. Verity Leads
  // and Website Leads are views of Drip Campaign now, and keep the audience they had as tabs: drip's.
  const canSeeTab: Record<EcmTab, boolean> = {
    campaigns: !isGraduateOnly && !isDripOnly,
    graduates: !isDripOnly,
    new_campaign: !isGraduateOnly && !isDripOnly,
    scheduler: canSeeScheduler && !isGraduateOnly && !isDripOnly,
    drip: canSeeDrip && !isGraduateOnly,
  };
  // The user's first tab. Always found: Graduates shows for everyone who is not drip-only, and Drip
  // Campaign for everyone who is.
  const homeTab = (Object.keys(TAB_PATHS) as EcmTab[]).find((t) => canSeeTab[t]) as EcmTab;
  // The tab the address asks for, roles aside.
  const requestedTab = ((): EcmTab => {
    if (seg === 'graduates') return 'graduates';
    if (seg === 'new-campaign') return 'new_campaign';
    if (seg === 'scheduler') return 'scheduler';
    // The retired Verity Leads and Website Leads addresses belong to Drip Campaign, with its audience.
    if (seg === 'drip' || movedView) return 'drip';
    if (legacyTemplates) return canSeeTab.new_campaign ? 'new_campaign' : canSeeDripLayout ? 'drip' : homeTab;
    return 'campaigns';
  })();
  // A tab the user cannot see is never selected. MUI showed the bar with no tab selected and an empty
  // page under it - a Campaign Manager typing a drip, Verity Leads or Website Leads address, anyone
  // without Scheduler.Admin on the scheduler's. The user's first tab renders from the first frame
  // instead, while the redirect below moves the address there, so nothing forbidden ever renders. The
  // URL is read for every role: a narrow role's tab is a DEFAULT, not an override - pinning Drip.Manager
  // to 'drip' before reading it once left Verity Leads inert, the address changing under a page that
  // kept rendering Drip Campaign.
  const activeTab: EcmTab = canSeeTab[requestedTab] ? requestedTab : homeTab;
  // The view inside Drip Campaign. A view the role cannot open falls back to the tab's first view, so
  // it renders something while the redirect below fixes the address.
  const dripView: DripView = (() => {
    if (movedView) return movedView;
    if (seg === 'drip' && (subSeg === 'leads' || subSeg === 'website')) return subSeg;
    if (canSeeDripLayout
        && ((seg === 'drip' && subSeg === 'layout-template') || (legacyTemplates && !canSeeTab.new_campaign))) {
      return 'layout-template';
    }
    return 'entries';
  })();
  // Drip Campaign's sub-tabs: its own two (the layout one for the roles that may edit it), then Verity
  // Leads and Website Leads: not in Verity, for everyone who sees the tab.
  const dripTabs: Array<{ value: DripView; label: string }> = [
    { value: 'entries', label: 'Drip Emails' },
    ...(canSeeDripLayout ? [{ value: 'layout-template' as const, label: 'Drip Layout Template' }] : []),
    { value: 'leads', label: 'Verity Leads' },
    { value: 'website', label: 'Website Leads: not in Verity' },
  ];
  // Keep a graduate-only user's URL on the graduates tab (they can't reach other ECM routes).
  useEffect(() => {
    if (isGraduateOnly && !location.pathname.endsWith('/graduates')) {
      navigate('/ECM/Dashboard/graduates', { replace: true });
    }
  }, [isGraduateOnly, location.pathname, navigate]);
  // Where an address this user should not stay on lands instead (null: stay). History is replaced,
  // so Back never re-enters it. The view above already matches the target, so nothing flashes.
  const redirectTo = ((): string | null => {
    if (isGraduateOnly) return null; // the effect above owns this role
    if (legacyTemplates) {
      return canSeeTab.new_campaign ? PORTAL_PATH : canSeeDripLayout ? DRIP_LAYOUT_PATH : TAB_PATHS[homeTab];
    }
    // A drip-only user keeps the views that are theirs - without that allow-list, opening Verity
    // Leads or Website Leads bounced straight back to Drip Campaign - and nothing else. Campaign
    // Portal is deliberately not on it. Their retired addresses go to the sub-tabs too.
    if (isDripOnly) return dripOnlyRedirect(location.pathname);
    // Any address under a tab this user cannot see (its sub-addresses too, such as scheduler/data).
    if (activeTab !== requestedTab) return TAB_PATHS[homeTab];
    // Campaign Portal has no sub-tabs any more. The retired SendGrid Templates sub-tab's address,
    // new-campaign/sendgrid-templates, keeps its route for old links and lands on New Campaign, which
    // already renders under it. Only that one segment: a future Campaign Portal sub-tab must be wired
    // on purpose, not swallowed here. Compared case-blind because React Router matches the route
    // case-blind too.
    if (seg === 'new-campaign' && subSeg.toLowerCase() === RETIRED_PORTAL_SUB) return PORTAL_PATH;
    // The retired Verity Leads and Website Leads tab addresses: their Drip Campaign sub-tab, which
    // already renders under them.
    if (movedTo) return movedTo;
    // A sub-tab the user cannot see, under a tab they can: that tab's first view.
    if (seg === 'drip' && subSeg === 'layout-template' && !canSeeDripLayout) return DRIP_PATH;
    return null;
  })();
  useEffect(() => {
    if (redirectTo && redirectTo !== location.pathname) navigate(redirectTo, { replace: true });
  }, [redirectTo, location.pathname, navigate]);
  const [jobs, setJobs] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('active'); // Filter: 'active', 'completed', 'archived', 'deleted'
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
  const [bulkActionInProgress, setBulkActionInProgress] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{open: boolean, action: 'archive' | 'delete' | 'restore_active' | 'restore_archived' | 'permanent_delete' | null}>({open: false, action: null});
  const [snackbar, setSnackbar] = useState<{open: boolean, message: string, severity: 'success' | 'error' | 'info'}>({open: false, message: '', severity: 'success'});
  const [manualCampaigns, setManualCampaigns] = useState<Array<{key: string; name: string; surveyTitle: string; createdAt: string}>>([]);
  const [currentTheme, setCurrentTheme] = useState(getSelectedTheme());

  // Apply theme on mount and when changed
  useEffect(() => {
    applyTheme(currentTheme);
  }, [currentTheme]);

  const handleThemeChange = (themeName: string) => {
    setCurrentTheme(themeName);
  };
  const [previewDialog, setPreviewDialog] = useState<{open: boolean, survey: any, campaignKey: string, campaignName: string}>({open: false, survey: null, campaignKey: '', campaignName: ''});
  const [convertDialog, setConvertDialog] = useState<{open: boolean, campaignKey: string, campaignName: string, survey: any}>({open: false, campaignKey: '', campaignName: '', survey: null});
  const [convertFile, setConvertFile] = useState<File | null>(null);
  const [converting, setConverting] = useState(false);
  const [responsesDialog, setResponsesDialog] = useState<{open: boolean, campaignId: string, campaignName: string, surveyId: string}>({open: false, campaignId: '', campaignName: '', surveyId: ''});
  const [responses, setResponses] = useState<any[]>([]);
  const [responsesLoading, setResponsesLoading] = useState(false);
  const [addRecipientsDialog, setAddRecipientsDialog] = useState<{open: boolean, campaignId: string, campaignName: string}>({open: false, campaignId: '', campaignName: ''});
  const [newRecipientEmail, setNewRecipientEmail] = useState('');
  const [newRecipientName, setNewRecipientName] = useState('');
  const [recipientFile, setRecipientFile] = useState<File | null>(null);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [diagnosticDialog, setDiagnosticDialog] = useState<{open: boolean, campaignId: string, campaignName: string, data: any}>({open: false, campaignId: '', campaignName: '', data: null});
  const [diagnosticLoading, setDiagnosticLoading] = useState(false);
  const [statusMenu, setStatusMenu] = useState<{anchorEl: null | HTMLElement, campaignId: string, currentStatus: string}>({anchorEl: null, campaignId: '', currentStatus: ''});
  
  // Check if user came from SIMS Admin
  const [showBackButton, setShowBackButton] = useState(false);
  const [userRole, setUserRole] = useState<string>('');
  
  useEffect(() => {
    // Get the current user from session storage
    const currentUser = getCurrentUser();
    if (currentUser && currentUser.role) {
      setUserRole(currentUser.role);
      console.log('👤 User role from getCurrentUser:', currentUser.role);
    }
    
    // Check URL parameter, referrer, or persistent session flag
    const urlParams = new URLSearchParams(window.location.search);
    const fromSims = urlParams.get('from') === 'sims';
    const simsEnv = urlParams.get('env');
    const referrer = document.referrer;
    const fromSimsReferrer = referrer.includes('qa-sims.lauruscollege.edu') || 
                            referrer.includes('sims.lauruscollege.edu');
    
    // Store persistent flag and origin URL if coming from SIMS
    if (fromSims || fromSimsReferrer) {
      sessionStorage.setItem('ecm_originated_from_sims', 'true');
      
      // Store the correct SIMS origin based on env parameter
      if (simsEnv === 'qa') {
        sessionStorage.setItem('ecm_sims_origin', 'https://qa-sims.lauruscollege.edu/Surveys');
        console.log('🎯 Stored QA SIMS origin');
      } else if (simsEnv === 'prod') {
        sessionStorage.setItem('ecm_sims_origin', 'https://sims.lauruscollege.edu/Surveys');
        console.log('🎯 Stored Prod SIMS origin');
      } else if (referrer.includes('qa-sims.lauruscollege.edu')) {
        sessionStorage.setItem('ecm_sims_origin', 'https://qa-sims.lauruscollege.edu/Surveys');
        console.log('🎯 Stored QA SIMS origin from referrer');
      } else if (referrer.includes('sims.lauruscollege.edu')) {
        sessionStorage.setItem('ecm_sims_origin', 'https://sims.lauruscollege.edu/Surveys');
        console.log('🎯 Stored Prod SIMS origin from referrer');
      }
    }
    
    // Check persistent flag
    const originatedFromSims = sessionStorage.getItem('ecm_originated_from_sims') === 'true';
    setShowBackButton(originatedFromSims);
    
    // Log what's stored
    const storedOrigin = sessionStorage.getItem('ecm_sims_origin');
    console.log('🔍 Current SIMS origin:', storedOrigin);
  }, []);

  // Continuously check for role in sessionStorage (for direct ECM login)
  useEffect(() => {
    // If role is already set, no need to poll
    if (userRole) {
      console.log('✅ User role already set:', userRole);
      return;
    }

    console.log('🔄 Starting to poll for user role from sessionStorage...');
    
    const checkRole = () => {
      const storedRole = sessionStorage.getItem('ecm_user_role');
      if (storedRole && storedRole !== userRole) {
        setUserRole(storedRole);
        console.log('✅ User role found and set:', storedRole);
        return true; // Role found
      }
      return false; // Role not found yet
    };

    // Check immediately on mount
    if (checkRole()) return;

    // More aggressive polling: check every 100ms for first 3 seconds, then every 500ms
    let checkCount = 0;
    const maxChecks = 50; // 3s @ 100ms + 4s @ 500ms = ~7 seconds total
    
    const interval = setInterval(() => {
      checkCount++;
      
      if (checkRole()) {
        clearInterval(interval);
        return;
      }
      
      // After 30 checks (3 seconds), slow down to 500ms
      if (checkCount === 30) {
        clearInterval(interval);
        const slowInterval = setInterval(() => {
          if (checkRole() || checkCount >= maxChecks) {
            clearInterval(slowInterval);
            if (checkCount >= maxChecks) {
              console.log('⏱️ Stopped polling for user role - max attempts reached');
            }
          }
          checkCount++;
        }, 500);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [userRole]);

  const loadJobs = async () => {
    try {
      setLoading(true);
      const currentUser = getCurrentUser();
      console.log('📊 [ECM Dashboard] Loading campaigns for user:', currentUser?.email, 'role:', currentUser?.role);
      console.log('📊 [ECM Dashboard] User object:', currentUser);
      
      const data = await getUserJobs();
      console.log('📊 [ECM Dashboard] API returned:', data.length, 'campaigns');
      console.log('📊 [ECM Dashboard] First campaign (if any):', data[0]);
      
      // Client-side filtering as backup (in case backend filtering fails)
      let filteredData = data;
      
      // Always exclude templates from the campaigns tab (handle boolean true and string "true"/"True")
      filteredData = filteredData.filter((job: any) => !job.is_template && job.is_template !== 'true' && job.is_template !== 'True');
      
      if (currentUser?.role === 'Survey.Creator') {
        const userId = currentUser.userId || currentUser.user_id || currentUser.id;
        const userEmail = currentUser.email;
        filteredData = data.filter(job => 
          job.creator_id === userId || 
          job.ownerId === userId ||
          job.creator_id === userEmail ||
          job.ownerId === userEmail
        );
        console.log('📊 [ECM Dashboard] Filtered to', filteredData.length, 'campaigns for Survey.Creator');
      }
      
      setJobs(filteredData);
      
      // Show notification if no campaigns found
      if (filteredData.length === 0) {
        console.warn('⚠️ [ECM Dashboard] No campaigns found for user');
        setSnackbar({
          open: true,
          message: `📋 No campaigns found. ${currentUser?.role === 'Survey.Creator' ? 'Create your first campaign!' : 'No campaigns exist yet.'}`,
          severity: 'info'
        });
      }
    } catch (error: any) {
      console.error('❌ [ECM Dashboard] Failed to load campaigns:', error);
      console.error('❌ [ECM Dashboard] Error details:', error.response?.data || error.message);
      setSnackbar({
        open: true,
        message: `❌ Failed to load campaigns: ${error.response?.data?.error || error.message || 'Unknown error'}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadManualCampaigns = () => {
    try {
      const campaigns: Array<{key: string; name: string; surveyTitle: string; createdAt: string}> = [];
      
      // Scan localStorage for survey_ keys
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('survey_')) {
          try {
            const data = localStorage.getItem(key);
            if (data) {
              const survey = JSON.parse(data);
              campaigns.push({
                key,
                name: key.replace('survey_', ''),
                surveyTitle: survey.title || 'Untitled Survey',
                createdAt: new Date().toISOString() // We don't have actual creation date
              });
            }
          } catch (err) {
            console.warn('Failed to parse localStorage survey:', key, err);
          }
        }
      }
      
      setManualCampaigns(campaigns);
      console.log('📋 Loaded', campaigns.length, 'manual input campaigns from localStorage');
    } catch (error) {
      console.error('❌ Failed to load manual campaigns:', error);
    }
  };

  const handleConvertToCSV = async () => {
    if (!convertFile || !convertDialog.survey) return;
    
    setConverting(true);
    try {
      // Use survey template ID and get metadata from the stored survey
      const templateId = 'd-survey-template';
      const templateName = `Survey: ${convertDialog.survey.title}`;
      
      // Get sender info from localStorage or use defaults
      const fromEmail = localStorage.getItem('lastFromEmail') || 'noreply@lauruscollege.edu';
      const fromName = localStorage.getItem('lastFromName') || 'Laurus College';
      
      // Upload campaign with CSV
      await uploadCampaign(
        convertFile,
        convertDialog.campaignName,
        templateId,
        templateName,
        fromEmail,
        fromName
      );
      
      // Remove from localStorage after successful upload
      localStorage.removeItem(convertDialog.campaignKey);
      
      // Refresh both lists
      loadJobs();
      loadManualCampaigns();
      
      setSnackbar({
        open: true,
        message: '✅ Campaign converted successfully!',
        severity: 'success'
      });
      
      setConvertDialog({open: false, campaignKey: '', campaignName: '', survey: null});
      setConvertFile(null);
    } catch (error: any) {
      console.error('❌ Failed to convert campaign:', error);
      setSnackbar({
        open: true,
        message: `❌ Failed to convert campaign: ${error.message || 'Unknown error'}`,
        severity: 'error'
      });
    } finally {
      setConverting(false);
    }
  };

  const handleAddSingleRecipient = async () => {
    if (!newRecipientEmail.trim() || !addRecipientsDialog.campaignId) return;
    
    setSendingEmails(true);
    try {
      // Send email to single recipient
      await axios.post(`/campaigns/${addRecipientsDialog.campaignId}/add-recipient`, {
        email: newRecipientEmail.trim(),
        name: newRecipientName.trim() || newRecipientEmail.trim()
      });
      
      setSnackbar({
        open: true,
        message: `✅ Email sent to ${newRecipientEmail}`,
        severity: 'success'
      });
      
      setNewRecipientEmail('');
      setNewRecipientName('');
      loadJobs(); // Refresh to update recipient count
    } catch (error: any) {
      console.error('❌ Failed to add recipient:', error);
      setSnackbar({
        open: true,
        message: `❌ Failed to send email: ${error.response?.data?.message || error.message}`,
        severity: 'error'
      });
    } finally {
      setSendingEmails(false);
    }
  };

  const handleAddRecipientsFromCSV = async () => {
    if (!recipientFile || !addRecipientsDialog.campaignId) return;
    
    setSendingEmails(true);
    try {
      // Upload CSV file to add recipients
      const formData = new FormData();
      formData.append('file', recipientFile);
      
      await axios.post(`/campaigns/${addRecipientsDialog.campaignId}/add-recipients-csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setSnackbar({
        open: true,
        message: `✅ Recipients added from CSV successfully!`,
        severity: 'success'
      });
      
      setAddRecipientsDialog({open: false, campaignId: '', campaignName: ''});
      setRecipientFile(null);
      loadJobs(); // Refresh to update recipient count
    } catch (error: any) {
      console.error('❌ Failed to add recipients:', error);
      setSnackbar({
        open: true,
        message: `❌ Failed to add recipients: ${error.response?.data?.message || error.message}`,
        severity: 'error'
      });
    } finally {
      setSendingEmails(false);
    }
  };

  useEffect(() => {
    // Narrow roles never touch campaigns — skip the initial load and the 10s poll (and its
    // "No campaigns found" toast) entirely. Drip.Manager belongs here as much as the graduate
    // roles do: it cannot see campaigns, so telling it none exist is noise on a screen about
    // something else, and it polls for data the role has no permission to read.
    if (isGraduateOnly || isDripOnly) return;
    // Wait a moment for any MSAL redirects to complete before loading campaigns
    const initializeAndLoad = async () => {
      try {
        // Give MSAL time to process any pending redirects
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Now load campaigns
        loadJobs();
        loadManualCampaigns();
      } catch (error) {
        console.error('❌ Failed to initialize ECM Dashboard:', error);
      }
    };
    
    initializeAndLoad();
    
    const interval = setInterval(() => {
      loadJobs();
      loadManualCampaigns();
    }, 10000); // Refresh every 10s
    return () => clearInterval(interval);
  }, [isGraduateOnly, isDripOnly]);

  const handleBulkArchive = async () => {
    setBulkActionInProgress(true);
    setConfirmDialog({open: false, action: null});
    
    try {
      const selectedCampaignIds = selectedRows as string[];
      const results = await Promise.allSettled(
        selectedCampaignIds.map(id => archiveCampaign(id))
      );
      
      const successCount = results.filter(r => r.status === 'fulfilled').length;
      const failCount = results.filter(r => r.status === 'rejected').length;
      
      setSnackbar({
        open: true,
        message: `Successfully archived ${successCount} campaign(s)${failCount > 0 ? `, ${failCount} failed` : ''}`,
        severity: failCount > 0 ? 'error' : 'success'
      });
      
      setSelectedRows([]);
      await loadJobs();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to archive campaigns',
        severity: 'error'
      });
    } finally {
      setBulkActionInProgress(false);
    }
  };

  const handleBulkDelete = async () => {
    setBulkActionInProgress(true);
    setConfirmDialog({open: false, action: null});
    
    try {
      const selectedCampaignIds = selectedRows as string[];
      const results = await Promise.allSettled(
        selectedCampaignIds.map(id => deleteCampaign(id))
      );
      
      const successCount = results.filter(r => r.status === 'fulfilled').length;
      const failCount = results.filter(r => r.status === 'rejected').length;
      
      setSnackbar({
        open: true,
        message: `Successfully deleted ${successCount} campaign(s)${failCount > 0 ? `, ${failCount} failed` : ''}`,
        severity: failCount > 0 ? 'error' : 'success'
      });
      
      setSelectedRows([]);
      await loadJobs();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to delete campaigns',
        severity: 'error'
      });
    } finally {
      setBulkActionInProgress(false);
    }
  };

  const handleBulkStatusChange = async (newStatus: 'active' | 'archived' | 'deleted') => {
    setBulkActionInProgress(true);
    setConfirmDialog({open: false, action: null});
    
    try {
      const selectedCampaignIds = selectedRows as string[];
      const results = await Promise.allSettled(
        selectedCampaignIds.map(id => updateCampaignStatus(id, newStatus))
      );
      
      const successCount = results.filter(r => r.status === 'fulfilled').length;
      const failCount = results.filter(r => r.status === 'rejected').length;
      
      const statusLabel = newStatus === 'active' ? 'restored to Active' : newStatus === 'archived' ? 'archived' : 'moved to Deleted';
      
      setSnackbar({
        open: true,
        message: `Successfully ${statusLabel} ${successCount} campaign(s)${failCount > 0 ? `, ${failCount} failed` : ''}`,
        severity: failCount > 0 ? 'error' : 'success'
      });
      
      setSelectedRows([]);
      await loadJobs();
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Failed to change campaign status`,
        severity: 'error'
      });
    } finally {
      setBulkActionInProgress(false);
    }
  };

  const handlePermanentDelete = async () => {
    setBulkActionInProgress(true);
    setConfirmDialog({open: false, action: null});
    
    try {
      const selectedCampaignIds = selectedRows as string[];
      const results = await Promise.allSettled(
        selectedCampaignIds.map(id => deleteCampaign(id))
      );
      
      const successCount = results.filter(r => r.status === 'fulfilled').length;
      const failCount = results.filter(r => r.status === 'rejected').length;
      
      setSnackbar({
        open: true,
        message: `Permanently deleted ${successCount} campaign(s)${failCount > 0 ? `, ${failCount} failed` : ''}. This action cannot be undone.`,
        severity: failCount > 0 ? 'error' : 'success'
      });
      
      setSelectedRows([]);
      await loadJobs();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to permanently delete campaigns',
        severity: 'error'
      });
    } finally {
      setBulkActionInProgress(false);
    }
  };

  const openConfirmDialog = (action: 'archive' | 'delete' | 'restore_active' | 'restore_archived' | 'permanent_delete') => {
    setConfirmDialog({open: true, action});
  };

  const handleCheckDuplicates = async (campaignId: string, campaignName: string) => {
    setDiagnosticDialog({open: true, campaignId, campaignName, data: null});
    setDiagnosticLoading(true);
    
    try {
      const result = await checkCampaignDuplicates(campaignId);
      setDiagnosticDialog({open: true, campaignId, campaignName, data: result});
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Failed to check duplicates: ${error.message || 'Unknown error'}`,
        severity: 'error'
      });
      setDiagnosticDialog({open: false, campaignId: '', campaignName: '', data: null});
    } finally {
      setDiagnosticLoading(false);
    }
  };

  const handleFixCounts = async (campaignId: string) => {
    setDiagnosticLoading(true);
    
    try {
      const result = await fixCampaignCounts(campaignId);
      setSnackbar({
        open: true,
        message: `Counts fixed successfully! Old: ${result.old_counts.emails_sent}/${result.old_counts.total_recipients}, New: ${result.new_counts.emails_sent}/${result.new_counts.total_recipients}`,
        severity: 'success'
      });
      setDiagnosticDialog({open: false, campaignId: '', campaignName: '', data: null});
      await loadJobs();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Failed to fix counts: ${error.message || 'Unknown error'}`,
        severity: 'error'
      });
    } finally {
      setDiagnosticLoading(false);
    }
  };

  const handleStatusMenuOpen = (event: React.MouseEvent<HTMLElement>, campaignId: string, currentStatus: string) => {
    setStatusMenu({anchorEl: event.currentTarget, campaignId, currentStatus});
  };

  const handleStatusMenuClose = () => {
    setStatusMenu({anchorEl: null, campaignId: '', currentStatus: ''});
  };

  const handleStatusChange = async (newStatus: string) => {
    const {campaignId} = statusMenu;
    handleStatusMenuClose();
    
    try {
      await updateCampaignStatus(campaignId, newStatus as 'active' | 'archived' | 'deleted');
      setSnackbar({
        open: true,
        message: `Campaign status changed to ${newStatus}`,
        severity: 'success'
      });
      await loadJobs();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Failed to change status: ${error.message || 'Unknown error'}`,
        severity: 'error'
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'processing':
      case 'active':
        return 'info';
      case 'failed':
        return 'error';
      case 'archived':
        return 'default';
      default:
        return 'default';
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'title',
      headerName: 'Campaign',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params: any) => {
        const getStatusStyle = (status: string) => {
          switch (status) {
            case 'completed':
              return { backgroundColor: 'var(--success)', color: '#ffffff' };
            case 'processing':
            case 'active':
              return { backgroundColor: 'var(--accent)', color: 'var(--button-text)' };
            case 'failed':
              return { backgroundColor: 'var(--error)', color: '#ffffff' };
            case 'archived':
              return { backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--card-border)' };
            default:
              return { backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--card-border)' };
          }
        };
        
        return (
          <Chip
            label={params.value}
            size="small"
            onClick={(e) => handleStatusMenuOpen(e, params.row.id, params.value)}
            sx={{ cursor: 'pointer', ...getStatusStyle(params.value) }}
          />
        );
      },
    },
    {
      field: 'progress',
      headerName: 'Progress',
      width: 180,
      renderCell: (params: any) => (
        <Box sx={{ width: '100%' }}>
          <LinearProgress
            variant="determinate"
            value={params.row.total_recipients > 0 
              ? (params.row.emails_sent / params.row.total_recipients) * 100 
              : 0}
            sx={{
              backgroundColor: 'var(--bg-secondary)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: 'var(--accent)'
              }
            }}
          />
          <Typography variant="caption" sx={{ color: 'var(--text-primary)' }}>
            {params.row.emails_sent} / {params.row.total_recipients}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 150,
      valueGetter: (params: any) => params.row.created_at || '',
      renderCell: (params: any) => {
        const date = params.row.created_at;
        if (!date) return 'N/A';
        // Parse ISO string as UTC and convert to user's local timezone
        const utcDate = new Date(date + (date.endsWith('Z') ? '' : 'Z'));
        return utcDate.toLocaleString('en-US', {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      minWidth: 350,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: any) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', py: 0.5 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => navigate(`/ECM/job/${params.row.id}`)}
            sx={{ 
              minWidth: 60,
              borderColor: 'var(--accent)',
              color: 'var(--accent)',
              '&:hover': {
                borderColor: 'var(--accent-hover)',
                backgroundColor: 'var(--bg-secondary)'
              }
            }}
          >
            View
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => navigate(`/ECM/Dashboard/new-campaign?edit=${params.row.id}`)}
            sx={{ 
              minWidth: 60,
              backgroundColor: 'var(--button-bg)',
              color: 'var(--button-text)',
              '&:hover': {
                backgroundColor: 'var(--button-hover)'
              }
            }}
          >
            Edit
          </Button>
        </Box>
      ),
    },
  ];

  const stats = {
    total: jobs.length,
    active: jobs.filter((j: Campaign) => j.status === 'active').length,
    completed: jobs.filter((j: Campaign) => j.status === 'completed').length,
    failed: jobs.filter((j: Campaign) => j.status === 'failed').length,
    archived: jobs.filter((j: Campaign) => j.status === 'archived').length,
    deleted: jobs.filter((j: Campaign) => j.status === 'deleted').length,
  };

  // Filter jobs based on current tab
  const filteredJobs = jobs.filter((j: Campaign) => {
    if (statusFilter === 'active') {
      if (j.status === 'archived' || j.status === 'deleted' || j.status === 'completed') return false;
    } else {
      if (j.status !== statusFilter) return false;
    }
    if (searchQuery && !j.title?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, var(--bg-gradient-start) 0%, var(--bg-gradient-end) 100%)',
      color: 'var(--text-primary)',
      '& .MuiPaper-root': {
        backgroundColor: 'var(--card-bg)',
        color: 'var(--text-primary)',
        borderColor: 'var(--card-border)'
      },
      '& .MuiDialog-paper': {
        backgroundColor: 'var(--card-bg)',
        color: 'var(--text-primary)',
        borderColor: 'var(--card-border)'
      },
      '& .MuiDialogTitle-root': {
        backgroundColor: 'var(--bg-secondary)',
        color: 'var(--text-primary)'
      },
      '& .MuiDialogContent-root': {
        color: 'var(--text-primary)'
      },
      '& .MuiDialogContentText-root': {
        color: 'var(--text-secondary)'
      },
      '& .MuiCard-root': {
        backgroundColor: 'var(--card-bg)',
        color: 'var(--text-primary)',
        borderColor: 'var(--card-border)'
      },
      '& .MuiCardContent-root': {
        color: 'var(--text-primary)'
      },
      '& .MuiButton-root': {
        color: 'var(--text-primary)'
      },
      '& .MuiButton-contained': {
        backgroundColor: 'var(--button-bg)',
        color: 'var(--button-text)',
        '&:hover': {
          backgroundColor: 'var(--button-hover)'
        }
      },
      '& .MuiTextField-root input, & .MuiTextField-root textarea': {
        color: 'var(--text-primary)'
      },
      '& .MuiInputLabel-root': {
        color: 'var(--text-secondary)'
      },
      '& .MuiInput-root::before': {
        borderColor: 'var(--input-border)'
      },
      '& .MuiInput-root:hover:not(.Mui-disabled)::before': {
        borderColor: 'var(--accent)'
      },
      '& .MuiInput-root::after': {
        borderColor: 'var(--accent)'
      },
      '& .MuiChip-root': {
        backgroundColor: 'var(--bg-secondary)',
        color: 'var(--text-primary)',
        borderColor: 'var(--card-border)'
      }
    }}>
      {/* Theme Selector - Fixed position at top right */}
      <Box sx={{ position: 'fixed', top: 20, right: 20, zIndex: 1000 }}>
        <ThemeSelector currentTheme={currentTheme} onThemeChange={handleThemeChange} />
      </Box>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ color: 'var(--text-primary)' }}>
            Campaign Dashboard
          </Typography>
          {userRole && (
            <Typography variant="subtitle1" sx={{ color: 'var(--text-secondary)', mt: 0.5 }}>
              {userRole === 'Survey.Admin' && '📊 SIMS Admin View'}
              {userRole === 'Campaign.Manager' && '📧 Campaign Manager View'}
              {userRole === 'Survey.Creator' && '✏️ Survey Creator View'}
              {userRole === 'Teachers' && '👨‍🏫 Teacher View'}
              {userRole === 'Faculty' && '👩‍🏫 Faculty View'}
              {userRole === 'Survey.Viewer' && '👀 Viewer'}
              {userRole === 'Graduate.Admin' && '🎓 Graduate Dashboard'}
              {userRole === 'Graduate.Viewer' && '🎓 Graduate Dashboard (View Only)'}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {showMyDashboards && (
            <Button
              variant="outlined"
              onClick={() => navigate('/Dashboard')}
              sx={{
                textTransform: 'none',
                borderColor: 'var(--accent)',
                color: 'var(--accent)',
                '&:hover': {
                  borderColor: 'var(--accent-hover)',
                  color: 'var(--accent-hover)',
                  backgroundColor: 'var(--accent-bg)'
                }
              }}
            >
              ⊞ My Dashboards
            </Button>
          )}
          {((showBackButton || userRole === 'Survey.Admin' || userRole === 'Survey.Creator') && userRole !== 'Campaign.Manager' && !isGraduateOnly) && (
            <Button
              variant="outlined"
              onClick={() => {
                const fromQa = document.referrer.includes('qa-sims');
                const base = fromQa
                  ? 'https://qa-sims.lauruscollege.edu'
                  : 'https://sims.lauruscollege.edu';
                const target = userRole === 'Survey.Creator' ? '/Surveys/creator' : '/Surveys';
                window.location.href = base + target;
              }}
              sx={{ 
                textTransform: 'none',
                borderColor: 'var(--accent)',
                color: 'var(--accent)',
                '&:hover': {
                  borderColor: 'var(--accent-hover)',
                  color: 'var(--accent-hover)',
                  backgroundColor: 'var(--accent-bg)'
                }
              }}
            >
              ← Go back to {userRole === 'Survey.Creator' ? 'My Surveys' : 'SIMS Admin Surveys'}
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadJobs}
            sx={{
              borderColor: 'var(--accent)',
              color: 'var(--accent)',
              '&:hover': {
                borderColor: 'var(--accent-hover)',
                color: 'var(--accent-hover)',
                backgroundColor: 'var(--accent-bg)'
              }
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Bulk Action Buttons */}
      {selectedRows.length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            {selectedRows.length} selected
          </Typography>
          {statusFilter === 'active' && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<ArchiveIcon />}
              onClick={() => openConfirmDialog('archive')}
              disabled={bulkActionInProgress}
            >
              Archive Selected
            </Button>
          )}
          {statusFilter === 'completed' && (
            <>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<ArchiveIcon />}
                onClick={() => openConfirmDialog('archive')}
                disabled={bulkActionInProgress}
              >
                Archive Selected
              </Button>
            </>
          )}
          {statusFilter === 'archived' && (
            <>
              <Button
                variant="outlined"
                color="success"
                startIcon={<RestoreIcon />}
                onClick={() => openConfirmDialog('restore_active')}
                disabled={bulkActionInProgress}
              >
                Restore to Active
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => openConfirmDialog('delete')}
                disabled={bulkActionInProgress}
              >
                Delete Selected
              </Button>
            </>
          )}
          {statusFilter === 'deleted' && (
            <>
              <Button
                variant="outlined"
                color="success"
                startIcon={<RestoreIcon />}
                onClick={() => openConfirmDialog('restore_archived')}
                disabled={bulkActionInProgress}
              >
                Move to Archived
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => openConfirmDialog('permanent_delete')}
                disabled={bulkActionInProgress}
                sx={{ ml: 1 }}
              >
                Permanently Delete
              </Button>
            </>
          )}
        </Box>
      )}

      {/* Main Navigation Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1.5 }}>
        {/* Scrollable: a narrow screen can still be narrower than every tab a Survey.Admin can
            hold, and the standard variant clips the far tabs with no way to reach them. Scroll
            buttons appear only when the tabs overflow. */}
        <Tabs
          value={activeTab}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          onChange={(_e, newValue: EcmTab) => navigate(TAB_PATHS[newValue])}
          TabIndicatorProps={{
            style: {
              backgroundColor: 'var(--accent)'
            }
          }}
          // Laptop tier: 13px labels, 6px padding and no letter-spacing, so all of a Survey.Admin's
          // tabs (five with Scheduler, since Verity Leads and Website Leads moved under Drip
          // Campaign) fit at 1366px and 1440px - and at 1280px, a 1920px laptop at 150% - instead of
          // the last one hiding behind the scroll arrow. Once MUI shows its two 40px arrows it keeps
          // them for as long as the tabs overflow the narrowed scroller, so the bar is kept inside
          // 983px, which still fits a 1366px laptop with the arrows showing.
          // Only colours animate. index.css gives every button "transition: all", which animated
          // the padding and size across the 1800px tier: for a quarter of a second after a resize
          // down from a wide screen the tabs were still wide, the arrows appeared, and at 1280px
          // they stayed.
          // From 1800px, the ultrawide included, MUI's own spacing, size and letter-spacing.
          sx={(theme) => ({
            '& .MuiTab-root': {
              px: 0.75, fontSize: '0.8125rem', letterSpacing: 0,
              transition: 'color 0.25s ease, background-color 0.25s ease',
            },
            '@media (min-width: 1800px)': {
              '& .MuiTab-root': {
                px: 2,
                fontSize: theme.typography.button.fontSize,
                letterSpacing: theme.typography.button.letterSpacing,
              },
            },
          })}
        >
          {/* The tab VALUES and the routes behind them are unchanged - only these labels moved
              ("Campaigns" -> "All Campaigns", "New Campaign" -> "Campaign Portal"), so existing
              links and bookmarks still land where people expect. */}
          {canSeeTab.campaigns && (
          <Tab
            label="All Campaigns"
            value="campaigns"
            sx={{
              color: 'var(--text-primary)',
              '&.Mui-selected': {
                color: 'var(--accent)',
              }
            }}
          />
          )}
          {canSeeTab.graduates && (
          <Tab
            label="Graduates"
            value="graduates"
            sx={{
              color: 'var(--text-primary)',
              '&.Mui-selected': {
                color: 'var(--accent)',
              }
            }}
          />
          )}
          {canSeeTab.new_campaign && (
          <Tab
            label="Campaign Portal"
            value="new_campaign"
            sx={{
              color: 'var(--text-primary)',
              '&.Mui-selected': {
                color: 'var(--accent)',
              }
            }}
          />
          )}
          {canSeeTab.scheduler && (
            <Tab
              label="🗓️ Scheduler"
              value="scheduler"
              sx={{
                color: 'var(--text-primary)',
                '&.Mui-selected': {
                  color: 'var(--accent)',
                }
              }}
            />
          )}
          {canSeeTab.drip && (
            <Tab
              label="💧 Drip Campaign"
              value="drip"
              sx={{
                color: 'var(--text-primary)',
                '&.Mui-selected': {
                  color: 'var(--accent)',
                }
              }}
            />
          )}
          {/* No Verity Leads or Website Leads tab: both are sub-tabs of Drip Campaign, with the same
              audience. Their old addresses, Dashboard/leads and Dashboard/website, redirect there. */}
          {/* No Templates tab: the drip layout is a sub-tab of Drip Campaign, and campaign templates
              are made in SendGrid. The old address, Dashboard/email-templates, redirects to Campaign
              Portal, or for a drip-only role to the drip layout. */}
        </Tabs>
      </Box>

      {/* Show Campaigns Tab Content */}
      {activeTab === 'campaigns' && (
        <Box sx={{ p: 3 }}>
      {/* Status Filter Tabs */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Chip
          label={`Active (${stats.active})`}
          onClick={() => setStatusFilter('active')}
          clickable
          sx={{
            backgroundColor: statusFilter === 'active' ? 'var(--accent)' : 'var(--card-bg)',
            color: statusFilter === 'active' ? 'var(--button-text)' : 'var(--text-primary)',
            border: '1px solid var(--card-border)',
            minWidth: 'fit-content',
            '& .MuiChip-label': {
              overflow: 'visible',
              whiteSpace: 'nowrap'
            },
            '&:hover': {
              backgroundColor: statusFilter === 'active' ? 'var(--accent-hover)' : 'var(--bg-secondary)'
            }
          }}
        />
        <Chip
          label={`Completed (${stats.completed})`}
          onClick={() => setStatusFilter('completed')}
          clickable
          sx={{
            backgroundColor: statusFilter === 'completed' ? 'var(--accent)' : 'var(--card-bg)',
            color: statusFilter === 'completed' ? 'var(--button-text)' : 'var(--text-primary)',
            border: '1px solid var(--card-border)',
            minWidth: 'fit-content',
            '& .MuiChip-label': {
              overflow: 'visible',
              whiteSpace: 'nowrap'
            },
            '&:hover': {
              backgroundColor: statusFilter === 'completed' ? 'var(--accent-hover)' : 'var(--bg-secondary)'
            }
          }}
        />
        <Chip
          label={`Archived (${stats.archived})`}
          onClick={() => setStatusFilter('archived')}
          clickable
          sx={{
            backgroundColor: statusFilter === 'archived' ? 'var(--accent)' : 'var(--card-bg)',
            color: statusFilter === 'archived' ? 'var(--button-text)' : 'var(--text-primary)',
            border: '1px solid var(--card-border)',
            minWidth: 'fit-content',
            '& .MuiChip-label': {
              overflow: 'visible',
              whiteSpace: 'nowrap'
            },
            '&:hover': {
              backgroundColor: statusFilter === 'archived' ? 'var(--accent-hover)' : 'var(--bg-secondary)'
            }
          }}
        />
        <Chip
          label={`Deleted (${stats.deleted})`}
          onClick={() => setStatusFilter('deleted')}
          clickable
          sx={{
            backgroundColor: statusFilter === 'deleted' ? 'var(--accent)' : 'var(--card-bg)',
            color: statusFilter === 'deleted' ? 'var(--button-text)' : 'var(--text-primary)',
            border: '1px solid var(--card-border)',
            minWidth: 'fit-content',
            '& .MuiChip-label': {
              overflow: 'visible',
              whiteSpace: 'nowrap'
            },
            '&:hover': {
              backgroundColor: statusFilter === 'deleted' ? 'var(--accent-hover)' : 'var(--bg-secondary)'
            }
          }}
        />
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper sx={{ 
            p: 1.5,
            backgroundColor: 'var(--card-bg)',
            borderColor: 'var(--card-border)',
            border: '1px solid var(--card-border)'
          }}>
            <Typography variant="h6" sx={{ color: 'var(--text-secondary)' }}>
              Total Campaigns
            </Typography>
            <Typography variant="h3" sx={{ color: 'var(--text-primary)' }}>{stats.total}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper sx={{ 
            p: 1.5,
            backgroundColor: 'var(--card-bg)',
            borderColor: 'var(--card-border)',
            border: '1px solid var(--card-border)'
          }}>
            <Typography variant="h6" sx={{ color: 'var(--accent)' }}>
              Active
            </Typography>
            <Typography variant="h3" sx={{ color: 'var(--text-primary)' }}>{stats.active}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper sx={{ 
            p: 1.5,
            backgroundColor: 'var(--card-bg)',
            borderColor: 'var(--card-border)',
            border: '1px solid var(--card-border)'
          }}>
            <Typography variant="h6" sx={{ color: 'var(--success)' }}>
              Completed
            </Typography>
            <Typography variant="h3" sx={{ color: 'var(--text-primary)' }}>{stats.completed}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper sx={{ 
            p: 1.5,
            backgroundColor: 'var(--card-bg)',
            borderColor: 'var(--card-border)',
            border: '1px solid var(--card-border)'
          }}>
            <Typography variant="h6" sx={{ color: 'var(--error)' }}>
              Failed
            </Typography>
            <Typography variant="h3" sx={{ color: 'var(--text-primary)' }}>{stats.failed}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper sx={{ 
            p: 1.5,
            backgroundColor: 'var(--card-bg)',
            borderColor: 'var(--card-border)',
            border: '1px solid var(--card-border)'
          }}>
            <Typography variant="h6" sx={{ color: 'var(--text-secondary)' }}>
              Archived
            </Typography>
            <Typography variant="h3" sx={{ color: 'var(--text-primary)' }}>{stats.archived}</Typography>
          </Paper>
        </Grid>
      </Grid>

      <TextField
        placeholder="Search campaigns by title..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        size="small"
        sx={{ mb: 2, width: 400 }}
      />

      <Paper sx={{ 
        height: 'calc(100vh - 320px)',
        width: '100%',
        backgroundColor: 'var(--card-bg)',
        borderColor: 'var(--card-border)',
        border: '1px solid var(--card-border)',
        padding: 0,
        overflow: 'hidden',
        '& .MuiDataGrid-root': {
          color: 'var(--text-primary)',
          border: 'none'
        },
        '& .MuiDataGrid-cell': {
          borderColor: 'var(--card-border)',
          color: 'var(--text-primary)'
        },
        '& .MuiDataGrid-columnHeaders': {
          backgroundColor: 'var(--bg-secondary)',
          color: 'var(--text-primary)',
          borderColor: 'var(--card-border)'
        },
        '& .MuiDataGrid-columnHeaderTitle': {
          color: 'var(--text-primary)',
          fontWeight: 600
        },
        '& .MuiDataGrid-columnSeparator': {
          color: 'var(--card-border)'
        },
        '& .MuiDataGrid-menuIcon': {
          color: 'var(--text-primary)'
        },
        '& .MuiDataGrid-sortIcon': {
          color: 'var(--text-primary)'
        },
        '& .MuiDataGrid-footerContainer': {
          backgroundColor: 'var(--bg-secondary)',
          borderColor: 'var(--card-border)',
          color: 'var(--text-primary)'
        },
        '& .MuiTablePagination-root': {
          color: 'var(--text-primary)'
        },
        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
          color: 'var(--text-primary)'
        },
        '& .MuiTablePagination-select': {
          color: 'var(--text-primary)'
        },
        '& .MuiTablePagination-actions button': {
          color: 'var(--text-primary)'
        },
        '& .MuiCheckbox-root': {
          color: 'var(--accent)'
        },
        '& .MuiDataGrid-row:hover': {
          backgroundColor: 'var(--bg-secondary)'
        }
      }}>
        <DataGrid
          rows={filteredJobs}
          columns={columns}
          loading={loading}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
            sorting: { sortModel: [{ field: 'created_at', sort: 'desc' }] },
          }}
          checkboxSelection
          rowSelectionModel={selectedRows}
          onRowSelectionModelChange={(newSelection) => setSelectedRows(newSelection)}
          disableRowSelectionOnClick
        />
      </Paper>

      {/* Manual Input Campaigns Section - HIDDEN: Not used with CSV workflow */}
      {false && manualCampaigns.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              📝 Manual Input Campaigns (Local Only)
            </Typography>
            <Typography variant="body2">
              These campaigns were created with manual input (no CSV) and are stored locally in your browser. 
              They won't sync across devices and will be lost if you clear browser data.
            </Typography>
          </Alert>

          <Grid container spacing={2}>
            {manualCampaigns.map((campaign) => (
              <Grid item xs={12} sm={6} md={4} key={campaign.key}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom noWrap>
                      {campaign.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Survey: {campaign.surveyTitle}
                    </Typography>
                    <Chip 
                      label="Local Only" 
                      size="small" 
                      color="warning"
                      sx={{ mt: 1 }}
                    />
                  </CardContent>
                  <CardActions>
                    <Tooltip title="View survey details">
                      <Button
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => {
                          try {
                            const data = localStorage.getItem(campaign.key);
                            if (data) {
                              const survey = JSON.parse(data);
                              setPreviewDialog({
                                open: true,
                                survey,
                                campaignKey: campaign.key,
                                campaignName: campaign.name
                              });
                            }
                          } catch (error) {
                            console.error('Failed to load survey:', error);
                          }
                        }}
                      >
                        Preview
                      </Button>
                    </Tooltip>
                    <Tooltip title="Upload CSV to convert to full campaign">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PublishIcon />}
                        onClick={() => {
                          try {
                            const data = localStorage.getItem(campaign.key);
                            if (data) {
                              const survey = JSON.parse(data);
                              setConvertDialog({
                                open: true,
                                campaignKey: campaign.key,
                                campaignName: campaign.name,
                                survey
                              });
                            }
                          } catch (error) {
                            console.error('Failed to load survey:', error);
                          }
                        }}
                      >
                        Convert
                      </Button>
                    </Tooltip>
                    <Tooltip title="Delete from local storage">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          if (window.confirm(`Delete "${campaign.name}" from local storage?`)) {
                            localStorage.removeItem(campaign.key);
                            loadManualCampaigns();
                            setSnackbar({
                              open: true,
                              message: 'Campaign deleted from local storage',
                              severity: 'success'
                            });
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({open: false, action: null})}
      >
        <DialogTitle>
          {confirmDialog.action === 'archive' && 'Archive Campaigns'}
          {confirmDialog.action === 'delete' && 'Delete Campaigns'}
          {confirmDialog.action === 'restore_active' && 'Restore to Active'}
          {confirmDialog.action === 'restore_archived' && 'Move to Archived'}
          {confirmDialog.action === 'permanent_delete' && '⚠️ Permanently Delete Campaigns'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.action === 'archive' && 
              `Are you sure you want to archive ${selectedRows.length} campaign(s)? Archived campaigns can be restored later.`
            }
            {confirmDialog.action === 'delete' && 
              `Are you sure you want to move ${selectedRows.length} campaign(s) to Deleted status? Deleted campaigns can still be restored.`
            }
            {confirmDialog.action === 'restore_active' && 
              `Are you sure you want to restore ${selectedRows.length} campaign(s) to Active status?`
            }
            {confirmDialog.action === 'restore_archived' && 
              `Are you sure you want to move ${selectedRows.length} campaign(s) to Archived status?`
            }
            {confirmDialog.action === 'permanent_delete' && (
              <>
                <strong style={{ color: 'var(--error)' }}>WARNING: This action cannot be undone!</strong>
                <br /><br />
                Are you sure you want to PERMANENTLY DELETE {selectedRows.length} campaign(s) from the database?
                <br /><br />
                All campaign data, recipients, responses, and history will be lost forever.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({open: false, action: null})}>
            Cancel
          </Button>
          <Button 
            onClick={() => {
              if (confirmDialog.action === 'archive') handleBulkArchive();
              else if (confirmDialog.action === 'delete') handleBulkDelete();
              else if (confirmDialog.action === 'restore_active') handleBulkStatusChange('active');
              else if (confirmDialog.action === 'restore_archived') handleBulkStatusChange('archived');
              else if (confirmDialog.action === 'permanent_delete') handlePermanentDelete();
            }}
            color={
              confirmDialog.action === 'delete' || confirmDialog.action === 'permanent_delete' ? 'error' :
              confirmDialog.action === 'archive' ? 'warning' : 'success'
            }
            variant="contained"
            autoFocus
          >
            {confirmDialog.action === 'archive' && 'Archive'}
            {confirmDialog.action === 'delete' && 'Move to Deleted'}
            {confirmDialog.action === 'restore_active' && 'Restore'}
            {confirmDialog.action === 'restore_archived' && 'Move to Archived'}
            {confirmDialog.action === 'permanent_delete' && 'Permanently Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={previewDialog.open}
        onClose={() => setPreviewDialog({open: false, survey: null, campaignKey: '', campaignName: ''})}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Preview: {previewDialog.campaignName}
        </DialogTitle>
        <DialogContent>
          {previewDialog.survey && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                This survey is stored locally in your browser. Upload a CSV to convert it to a full campaign.
              </Alert>
              
              <Typography variant="h6" gutterBottom>
                {previewDialog.survey.title}
              </Typography>
              
              {previewDialog.survey.description && (
                <Typography variant="body2" color="text.secondary" paragraph>
                  {previewDialog.survey.description}
                </Typography>
              )}
              
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Sections: {previewDialog.survey.sections?.length || 0}
              </Typography>
              
              {previewDialog.survey.sections?.map((section: any, idx: number) => (
                <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {idx + 1}. {section.name}
                  </Typography>
                  {section.description && (
                    <Typography variant="body2" color="text.secondary">
                      {section.description}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Questions: {section.questions?.length || 0}
                  </Typography>
                  
                  {section.questions?.map((question: any, qIdx: number) => (
                    <Box key={qIdx} sx={{ ml: 2, mt: 1, p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                      <Typography variant="body2">
                        <strong>{qIdx + 1}.</strong> {question.question}
                      </Typography>
                      <Chip 
                        label={question.type} 
                        size="small" 
                        sx={{ mt: 0.5 }}
                      />
                      {question.required && (
                        <Chip 
                          label="Required" 
                          size="small" 
                          color="error" 
                          sx={{ mt: 0.5, ml: 0.5 }}
                        />
                      )}
                    </Box>
                  ))}
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialog({open: false, survey: null, campaignKey: '', campaignName: ''})}>
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<PublishIcon />}
            onClick={() => {
              const { survey, campaignKey, campaignName } = previewDialog;
              setPreviewDialog({open: false, survey: null, campaignKey: '', campaignName: ''});
              setConvertDialog({ open: true, campaignKey, campaignName, survey });
            }}
          >
            Convert to Campaign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Convert to CSV Campaign Dialog */}
      <Dialog
        open={convertDialog.open}
        onClose={() => !converting && setConvertDialog({open: false, campaignKey: '', campaignName: '', survey: null})}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Convert to CSV Campaign
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Upload a CSV file with recipient information to convert "{convertDialog.campaignName}" into a full campaign in the database.
          </Alert>
          
          <Typography variant="body2" paragraph>
            <strong>Required CSV columns:</strong> email, firstName, lastName
          </Typography>
          
          <Paper
            sx={{
              p: 3,
              textAlign: 'center',
              border: '2px dashed',
              borderColor: convertFile ? 'var(--accent)' : 'var(--card-border)',
              cursor: 'pointer',
              backgroundColor: convertFile ? 'var(--bg-secondary)' : 'var(--card-bg)',
              '&:hover': { backgroundColor: 'var(--bg-secondary)' }
            }}
            onClick={() => {
              const input = document.createElement('input');
              input.type = 'file';
              input.accept = '.csv';
              input.onchange = (e: any) => {
                const file = e.target.files?.[0];
                if (file) {
                  setConvertFile(file);
                }
              };
              input.click();
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 48, color: convertFile ? 'var(--accent)' : 'var(--text-secondary)' }} />
            <Typography variant="body1" sx={{ mt: 1 }}>
              {convertFile ? convertFile.name : 'Click to select CSV file'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {convertFile ? `${(convertFile.size / 1024).toFixed(2)} KB` : 'Drag & drop or click to browse'}
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setConvertDialog({open: false, campaignKey: '', campaignName: '', survey: null});
              setConvertFile(null);
            }}
            disabled={converting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConvertToCSV}
            disabled={!convertFile || converting}
            variant="contained"
            startIcon={<PublishIcon />}
          >
            {converting ? 'Converting...' : 'Convert Campaign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Responses Dialog */}
      <Dialog
        open={responsesDialog.open}
        onClose={() => setResponsesDialog({open: false, campaignId: '', campaignName: '', surveyId: ''})}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Survey Responses: {responsesDialog.campaignName}
        </DialogTitle>
        <DialogContent>
          <ViewResponses 
            surveyId={responsesDialog.surveyId}
            onClose={() => setResponsesDialog({open: false, campaignId: '', campaignName: '', surveyId: ''})}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResponsesDialog({open: false, campaignId: '', campaignName: '', surveyId: ''})}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Recipients Dialog */}
      <Dialog
        open={addRecipientsDialog.open}
        onClose={() => {
          setAddRecipientsDialog({open: false, campaignId: '', campaignName: ''});
          setNewRecipientEmail('');
          setNewRecipientName('');
          setRecipientFile(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Recipients to Campaign</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Campaign: <strong>{addRecipientsDialog.campaignName}</strong>
          </Typography>
          
          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
            Option 1: Add Single Email
          </Typography>
          <Box component="form" onSubmit={(e: React.FormEvent) => {
            e.preventDefault();
            handleAddSingleRecipient();
          }}>
            <TextField
              type="text"
              placeholder="Recipient Name"
              value={newRecipientName}
              onChange={(e) => setNewRecipientName(e.target.value)}
              fullWidth
              size="small"
              sx={{
                mb: 1,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'var(--input-bg)',
                  color: 'var(--text-primary)',
                  '& fieldset': {
                    borderColor: 'var(--input-border)'
                  },
                  '&:hover fieldset': {
                    borderColor: 'var(--accent)'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'var(--input-focus)'
                  }
                }
              }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                type="email"
                placeholder="recipient@example.com"
                value={newRecipientEmail}
                onChange={(e) => setNewRecipientEmail(e.target.value)}
                required
                fullWidth
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'var(--input-bg)',
                    color: 'var(--text-primary)',
                    '& fieldset': {
                      borderColor: 'var(--input-border)'
                    },
                    '&:hover fieldset': {
                      borderColor: 'var(--accent)'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'var(--input-focus)'
                    }
                  }
                }}
              />
              <Button 
                type="submit"
                variant="contained"
                disabled={sendingEmails || !newRecipientEmail.trim()}
              >
                Add
              </Button>
            </Box>
          </Box>

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
            Option 2: Upload CSV File
          </Typography>
          <Paper
            sx={{
              p: 3,
              textAlign: 'center',
              border: '2px dashed var(--card-border)',
              cursor: 'pointer',
              backgroundColor: 'var(--card-bg)',
              '&:hover': { backgroundColor: 'var(--bg-secondary)' }
            }}
            onClick={() => document.getElementById('recipient-csv-upload')?.click()}
          >
            <input
              type="file"
              accept=".csv"
              id="recipient-csv-upload"
              style={{ display: 'none' }}
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setRecipientFile(e.target.files[0]);
                }
              }}
            />
            <CloudUploadIcon sx={{ fontSize: 48, color: 'var(--text-secondary)', mb: 1 }} />
            <Typography variant="body1" sx={{ mt: 1 }}>
              {recipientFile ? recipientFile.name : 'Click to select CSV file'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              CSV should have an "email" column
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setAddRecipientsDialog({open: false, campaignId: '', campaignName: ''});
              setNewRecipientEmail('');
              setRecipientFile(null);
            }}
            disabled={sendingEmails}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddRecipientsFromCSV}
            disabled={!recipientFile || sendingEmails}
            variant="contained"
          >
            {sendingEmails ? 'Sending...' : 'Upload & Send'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diagnostic Dialog */}
      <Dialog
        open={diagnosticDialog.open}
        onClose={() => !diagnosticLoading && setDiagnosticDialog({open: false, campaignId: '', campaignName: '', data: null})}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Campaign Diagnostic: {diagnosticDialog.campaignName}</DialogTitle>
        <DialogContent>
          {diagnosticLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : diagnosticDialog.data ? (
            <Box>
              {/* Stored Counts */}
              <Typography variant="h6" sx={{ mt: 2, mb: 1, color: 'var(--text-primary)' }}>Stored Counts</Typography>
              <Paper sx={{ p: 2, backgroundColor: 'var(--bg-secondary)' }}>
                <Typography sx={{ color: 'var(--text-primary)' }}>Total Recipients: {diagnosticDialog.data.stored_counts.total_recipients}</Typography>
                <Typography sx={{ color: 'var(--text-primary)' }}>Emails Sent: {diagnosticDialog.data.stored_counts.emails_sent}</Typography>
                <Typography sx={{ color: 'var(--text-primary)' }}>Emails Failed: {diagnosticDialog.data.stored_counts.emails_failed}</Typography>
              </Paper>

              {/* Actual Counts */}
              <Typography variant="h6" sx={{ mt: 2, mb: 1, color: 'var(--text-primary)' }}>Actual Counts (from recipients array)</Typography>
              <Paper sx={{ p: 2, backgroundColor: 'var(--bg-secondary)' }}>
                <Typography sx={{ color: 'var(--text-primary)' }}>Total Recipient Records: {diagnosticDialog.data.actual_counts.total_recipient_records}</Typography>
                <Typography sx={{ color: 'var(--text-primary)' }}>Sent Records: {diagnosticDialog.data.actual_counts.sent_records}</Typography>
                <Typography sx={{ color: 'var(--text-primary)' }}>Unsent Records: {diagnosticDialog.data.actual_counts.unsent_records}</Typography>
                <Typography fontWeight="bold" sx={{ mt: 1, color: 'var(--text-primary)' }}>Unique Emails Total: {diagnosticDialog.data.actual_counts.unique_emails_total}</Typography>
                <Typography fontWeight="bold" sx={{ color: 'var(--text-primary)' }}>Unique Sent Emails: {diagnosticDialog.data.actual_counts.unique_sent_emails}</Typography>
                <Typography fontWeight="bold" sx={{ color: 'var(--text-primary)' }}>Unique Unsent Emails: {diagnosticDialog.data.actual_counts.unique_unsent_emails}</Typography>
              </Paper>

              {/* Duplicates */}
              {diagnosticDialog.data.duplicates.has_duplicates && (
                <>
                  <Typography variant="h6" sx={{ mt: 2, mb: 1, color: 'var(--warning)' }}>
                    Duplicates Found ({diagnosticDialog.data.duplicates.duplicate_count} duplicates)
                  </Typography>
                  <Paper sx={{ p: 2, backgroundColor: 'var(--warning)', opacity: 0.8 }}>
                    {Object.entries(diagnosticDialog.data.duplicates.duplicate_details).map(([email, details]: [string, any]) => (
                      <Box key={email} sx={{ mb: 1 }}>
                        <Typography fontWeight="bold" sx={{ color: '#000000' }}>{email} (appears {details.count} times)</Typography>
                        <Typography variant="body2" sx={{ color: '#000000' }}>
                          Sent: {details.sent_count}, Unsent: {details.unsent_count}
                        </Typography>
                      </Box>
                    ))}
                  </Paper>
                </>
              )}

              {/* Unsent Sample */}
              {diagnosticDialog.data.unsent_sample.length > 0 && (
                <>
                  <Typography variant="h6" sx={{ mt: 2, mb: 1, color: 'var(--text-primary)' }}>
                    Unsent Recipients (first {diagnosticDialog.data.unsent_sample.length})
                  </Typography>
                  <Paper sx={{ p: 2, backgroundColor: 'var(--bg-secondary)', maxHeight: 200, overflow: 'auto' }}>
                    {diagnosticDialog.data.unsent_sample.map((recipient: any, index: number) => (
                      <Typography key={index} variant="body2" sx={{ color: 'var(--text-primary)' }}>
                        {recipient.email} - {recipient.firstName} {recipient.lastName}
                        {recipient.lastError && ` (Error: ${recipient.lastError})`}
                      </Typography>
                    ))}
                  </Paper>
                </>
              )}

              {/* Recommendations */}
              {diagnosticDialog.data.recommendations.length > 0 && (
                <>
                  <Typography variant="h6" sx={{ mt: 2, mb: 1, color: 'var(--text-primary)' }}>Recommendations</Typography>
                  <Paper sx={{ p: 2, bgcolor: 'info.light' }}>
                    {diagnosticDialog.data.recommendations.map((rec: string, index: number) => (
                      <Typography key={index} variant="body2" sx={{ mb: 0.5, color: '#000000' }}>
                        • {rec}
                      </Typography>
                    ))}
                  </Paper>
                </>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDiagnosticDialog({open: false, campaignId: '', campaignName: '', data: null})}
            disabled={diagnosticLoading}
          >
            Close
          </Button>
          {diagnosticDialog.data?.duplicates?.has_duplicates && (
            <Button
              onClick={() => handleFixCounts(diagnosticDialog.campaignId)}
              disabled={diagnosticLoading}
              variant="contained"
              sx={{
                backgroundColor: 'var(--button-bg)',
                color: 'var(--button-text)',
                '&:hover': {
                  backgroundColor: 'var(--button-hover)'
                }
              }}
            >
              {diagnosticLoading ? 'Fixing...' : 'Fix Counts'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Status Change Menu */}
      <Menu
        anchorEl={statusMenu.anchorEl}
        open={Boolean(statusMenu.anchorEl)}
        onClose={handleStatusMenuClose}
      >
        <MenuItem onClick={() => handleStatusChange('active')} disabled={statusMenu.currentStatus === 'active'}>
          Active
        </MenuItem>
        <MenuItem onClick={() => handleStatusChange('completed')} disabled={statusMenu.currentStatus === 'completed'}>
          Completed
        </MenuItem>
        <MenuItem onClick={() => handleStatusChange('archived')} disabled={statusMenu.currentStatus === 'archived'}>
          Archived
        </MenuItem>
        <MenuItem onClick={() => handleStatusChange('failed')} disabled={statusMenu.currentStatus === 'failed'}>
          Failed
        </MenuItem>
      </Menu>

        </Box>
      )}

      {/* Show Graduates Tab Content */}
      {activeTab === 'graduates' && (
        <GraduateSurveys currentTheme={currentTheme} />
      )}

      {/* Campaign Portal: New Campaign alone, with no sub-tab bar. Its template pickers list the
          dynamic templates staff create in SendGrid. */}
      {activeTab === 'new_campaign' && (
        <Box sx={{ p: 3 }}>
          <NewCampaign />
        </Box>
      )}

      {/* Show Scheduler Tab Content (Scheduler.Admin only) */}
      {activeTab === 'scheduler' && canSeeScheduler && (
        <Box sx={{ p: 3 }}>
          <SchedulerAdminDashboard />
        </Box>
      )}

      {/* Show Drip Campaign Tab Content (Drip.Manager / Survey.Admin). The role is checked here as
          well as on the tab, so typing the URL does not reach it either. */}
      {activeTab === 'drip' && canSeeDrip && (
        <>
          {/* Drip Emails; the layout every drip email is sent in (drip_layout templates: Survey.Admin
              and Drip.Manager, the same audience as the tab - never campaign templates); Verity Leads;
              Website Leads: not in Verity. Each view's address has its child route in App.tsx. */}
          <SubTabs
            label="Drip Campaign"
            value={dripView}
            tabs={dripTabs}
            onChange={(v) => navigate(DRIP_VIEW_PATHS[v])}
          />
          {dripView === 'entries' && (
            <Box sx={{ p: 3, '@media (min-width: 2400px)': { px: 5 } }}>
              <DripCampaigns />
            </Box>
          )}
          {dripView === 'layout-template' && canSeeDripLayout && (
            <EmailTemplates purpose="drip_layout" />
          )}
          {/* Verity Leads. Inside the tab's role gate, so typing the URL does not reach it either. */}
          {dripView === 'leads' && <VerityLeads />}
          {/* Website inquiries Verity never accepted. The same gate. */}
          {dripView === 'website' && <WebsiteUnrouted />}
        </>
      )}

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({...snackbar, open: false})}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({...snackbar, open: false})} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
